import React, { useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { Heart, BookOpen, PieChart, User, Flag, Users, Home } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { filterMenuByRole } from "../utils/roleUtils";

const SidebarContainer = styled.div`
  width: 250px;
  height: 100vh;
  background-color: #404E67;
  position: fixed;
  left: 0;
  top: 0;
  display: flex;
  flex-direction: column;
  font-family: 'Prompt', sans-serif;
  z-index: 1000;
`;

const LogoSection = styled.div`
  padding: 1.5rem 1rem;
  border-bottom: 1px solid #515F77;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Logo1 = styled.img`
  width: 40px;
  object-fit: contain;
`;

const Logo2 = styled.img`
  width: 20px;
  object-fit: contain;
`;

const Logo3 = styled.img`
  width: 60px;
  object-fit: contain;
`;

const MenuSection = styled.div`
  padding: 1.5rem 0;
  flex: 1;
`;

const MenuTitle = styled.h3`
  color: #9CA3AF;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin: 0 0 1rem 0;
  padding: 0 1rem;
`;

const MenuItem = styled.div`
  display: flex;
  align-items: center;
  padding: 0.875rem 1rem;
  color: ${props => props.active ? '#FFFFFF' : '#C1C9D2'};
  background-color: ${props => props.active ? '#0F172A' : 'transparent'};
  border-right: ${props => props.active ? '3px solid #06B6D4' : '3px solid transparent'};
  cursor: pointer;
  transition: all 0.2s ease;
  gap: 0.75rem;
  margin: 0.125rem 0;

  &:hover {
    background-color: ${props => props.active ? '#0F172A' : '#515F77'};
    color: #FFFFFF;
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
    flex-shrink: 0;
  }
`;

const MenuText = styled.span`
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.4;
`;

const SubText = styled.div`
  font-size: 0.75rem;
  color: #9CA3AF;
  margin-top: 0.125rem;
`;

const Sidebar = ({ activeMenu, onMenuChange }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // กำหนดเมนูทั้งหมด พร้อม path สำหรับเช็ค role
  const allMenuItems = [
    {
      id: 'health-groups',
      icon: User,
      title: 'กลุ่มผู้รับการตรวจสุขภาพ/ admin องค์กร',
      subtitle: '',
      path: '/health-groups'
    },
    {
      id: 'campaigns',
      icon: Flag,
      title: 'จัดการบริษัท',
      subtitle: '',
      path: '/campaigns'
    },
    {
      id: 'user-management',
      icon: Users,
      title: 'จัดการผู้ใช้งาน/ ที่ปรึกษา',
      subtitle: '',
      path: '/user-management'
    },
    {
      id: 'health-record',
      icon: Heart,
      title: 'สมุดสุขภาพ',
      subtitle: '(ดู/บันทึก/แก้ไข/ส่งข้อมูล)',
      path: '/healthbook'
    },
    {
      id: 'setup-template',
      icon: BookOpen,
      title: 'setup template การแปลผลสุขภาพ',
      subtitle: '',
      path: '/template'
    },
    {
      id: 'dashboard-organization',
      icon: Home,
      title: 'Dashboard องค์กร',
      subtitle: '',
      path: '/dashboard-organization'
    },
  ];

  // กรองเมนูตาม role ของ user
  const userRoles = user?.role || [];
  const userLevel = user?.level || null; // ⭐ ดึง level จาก user
  const menuItems = filterMenuByRole(allMenuItems, userRoles, userLevel);

  return (
    <SidebarContainer>
      {/* Logo Section */}
      <LogoSection>
        <Logo1 src="/logo_Mfu.png" alt="MFU logo" />
        <Logo2 src="/stack-logo-light.png" alt="Stack Logo" />
        <Logo3 src="/logo_dietz2.png" alt="Dietz Logo 2" />
      </LogoSection>

      {/* Menu Section */}
      <MenuSection>
        <MenuTitle>GENERAL</MenuTitle>
        <MenuTitle>Super admin</MenuTitle>
        
        {menuItems.map((item) => {
          const IconComponent = item.icon;
          return (
            <MenuItem
              key={item.id}
              active={activeMenu === item.id}
              onClick={() => {
                onMenuChange && onMenuChange(item.id);
                navigate(item.path);
              }}
            >
              <IconComponent />
              <div>
                <MenuText>{item.title}</MenuText>
                {item.subtitle && <SubText>{item.subtitle}</SubText>}
              </div>
            </MenuItem>
          );
        })}

        {/* แสดงข้อความถ้าไม่มีเมนูให้เข้าถึง */}
        {menuItems.length === 0 && (
          <div style={{ padding: '1rem', color: '#9CA3AF', fontSize: '0.875rem' }}>
            ไม่มีเมนูที่สามารถเข้าถึงได้
          </div>
        )}
      </MenuSection>
    </SidebarContainer>
  );
};

export default Sidebar;